import type { FC } from 'react';
import {
    Button,
    ButtonGroup,
    ModalBody,
    ModalFooter,
    SmallModal,
} from '@george-labs.com/design-system';

import { t } from '@luna/i18n';

import { useImageUploadContext } from '../ImageUploadContext';
import { ImageUploadModal } from '../ImageUploadService';

interface Props {
    onImageDataUrlChange?: (image?: string) => void;
    onUploadedImageChange: () => void;
    onImageRemove: () => Promise<void>;
}

export const ImageRemoveModal: FC<Props> = ({
    onImageDataUrlChange,
    onUploadedImageChange,
    onImageRemove,
}) => {
    const { currentModal, closeModal, openLoadingModal, openErrorModal, openRemoveSuccessModal } =
        useImageUploadContext();

    if (currentModal !== ImageUploadModal.RemovalModal) {
        return null;
    }

    const handleImageRemoval = async () => {
        openLoadingModal();
        onImageDataUrlChange?.(undefined);

        onImageRemove()
            .then(() => {
                onUploadedImageChange();
                openRemoveSuccessModal();
            })
            .catch(openErrorModal);
    };

    return (
        <SmallModal
            isOpen
            appear
            variant={SmallModal.VARIANT.ERROR}
            title={t('main.imageUpload.removeImageModalTitle')}
            onClose={closeModal}
            data-cy="prompt-image-removal"
        >
            <ModalBody>{t('main.imageUpload.removeImageModalBody')}</ModalBody>
            <ModalFooter>
                <ButtonGroup>
                    <Button
                        data-cy="cancel-image-removal-button"
                        onClick={closeModal}
                        variant={Button.VARIANT.SECONDARY}
                    >
                        {t('app.cancel')}
                    </Button>
                    <Button data-cy="confirm-image-removal-button" onClick={handleImageRemoval}>
                        {t('app.delete')}
                    </Button>
                </ButtonGroup>
            </ModalFooter>
        </SmallModal>
    );
};
